// BM-H80 P2 — Client-forms axios shim.
//
// The client-facing form pages read the token from the URL query
// string; the fee-earner review pages carry ``X-Bonsai-User-Id`` in
// their headers via the existing auth flow. This shim owns the two
// header shapes so the pages do not repeat the boilerplate.

import axios from "axios";
import { API } from "../config/api";

const BASE = `${API}/bonsai/client-forms`;

function bearer(token) {
  return { Authorization: `Bearer ${token}` };
}

function feeEarner() {
  const uid =
    sessionStorage.getItem("bonsai_user_id") ||
    localStorage.getItem("bonsai_user_id") ||
    "";
  return uid ? { "X-Bonsai-User-Id": uid } : {};
}


// BM-H339: the questionnaire create endpoints refuse a send with HTTP 403 and
// a structured detail body when the matter's file is not properly open. This
// turns that body into the text the fee earner reads, so the message lives in
// one place and can be tested without mounting a page. Never returns an
// object, because rendering one would crash the card.
export function formatSendRefusal(detail, label = "questionnaire") {
  const fallback = `Could not create the ${label} questionnaire.`;
  if (!detail) return fallback;
  if (typeof detail === "string") return detail;
  const lines = Array.isArray(detail.outstanding) ? detail.outstanding : [];
  return [detail.message || fallback]
    .concat(lines.map((l) => `\u2022 ${l}`))
    .join("\n");
}

export const clientFormsApi = {
  createWillsForm: (payload) =>
    axios.post(`${BASE}/wills/create`, payload, { headers: feeEarner() }),

  // BM-H90: LPA questionnaire create endpoint lives under a sibling router
  // (backend/bonsai/client_forms/lpa_routes.py) at `/bonsai/client-forms/lpa/create`.
  createLpaForm: (payload) =>
    axios.post(`${BASE}/lpa/create`, payload, { headers: feeEarner() }),

  // BM-H350: the Estate Planning Review questionnaire is the workbook
  // router, not a client-forms sibling. This shim used to post the review
  // to a client-forms path that has never existed on the backend, so the
  // button was dead in production. The live route is POST
  // /api/epr/workbook/init, which carries the same BM-H339 file-open gate
  // as its wills and LPA siblings and takes the same payload shape the
  // matter page already sends.
  createEprForm: (payload) =>
    axios.post(`${API}/epr/workbook/init`, payload, { headers: feeEarner() }),

  // BM-H350: review questionnaires live in their own collection, so the
  // matter page has to ask for them separately or the card reports
  // "Not sent" for a link that has already gone to the client. Rows come
  // back in the same shape as the wills and LPA ones.
  listEprForMatter: (matterRef) =>
    axios.get(`${API}/epr/workbook/matters/${encodeURIComponent(matterRef)}`, {
      headers: feeEarner(),
    }),

  // BM-H4003: who the questionnaire will be sent in the name of on this matter, read from the matter. The send prompt
  // SHOWS this person; nobody types a fee earner any more. { determined, fee_earner_email, fee_earner_name, message }
  getMatterFeeEarner: (matterRef) =>
    axios.get(`${BASE}/matters/${encodeURIComponent(matterRef)}/fee-earner`, {
      headers: feeEarner(),
    }),

  listForMatter: (matterRef) =>
    axios.get(`${BASE}/matters/${encodeURIComponent(matterRef)}`, {
      headers: feeEarner(),
    }),

  revokeToken: (matterRef, fid) =>
    axios.post(
      `${BASE}/matters/${encodeURIComponent(matterRef)}/revoke-token/${fid}`,
      {},
      { headers: feeEarner() }
    ),

  extendExpiry: (matterRef, fid, additionalDays) =>
    axios.post(
      `${BASE}/matters/${encodeURIComponent(matterRef)}/extend-expiry/${fid}`,
      { additional_days: additionalDays },
      { headers: feeEarner() }
    ),

  resendToken: (matterRef, fid, ttlDays) =>
    axios.post(
      `${BASE}/matters/${encodeURIComponent(matterRef)}/resend-token/${fid}`,
      { ttl_days: ttlDays },
      { headers: feeEarner() }
    ),

  finaliseForm: (matterRef, fid, sharepointUploadRef) =>
    axios.post(
      `${BASE}/matters/${encodeURIComponent(matterRef)}/finalise-form/${fid}`,
      { sharepoint_upload_ref: sharepointUploadRef || null },
      { headers: feeEarner() }
    ),

  // BM-H3615: re-open a submitted questionnaire so the client can change an
  // answer. Answers are locked once the client submits. Only a submitted or
  // under-review form can be re-opened (409 otherwise).
  reopenForm: (matterRef, fid) =>
    axios.post(
      `${BASE}/matters/${encodeURIComponent(matterRef)}/reopen-form/${fid}`,
      {},
      { headers: feeEarner() }
    ),

  // BM-H3607: assemble the Will draft and file it to the matter's Drafts
  // folder. 200 only when it was assembled and the upload was confirmed; 409
  // lists every reason it could not be assembled, 502 means it was assembled
  // but not filed.
  draftWill: (matterRef, fid) =>
    axios.post(
      `${BASE}/matters/${encodeURIComponent(matterRef)}/draft-will/${fid}`,
      {},
      { headers: feeEarner() }
    ),

  // BM-H3756 / BM-H3634 / BM-H3879: what a fee earner has confirmed on a Will, and recording one. The
  // server uses the signed-in person (never the body) and stamps the date.
  getConfirmations: (matterRef, fid) =>
    axios.get(`${BASE}/matters/${encodeURIComponent(matterRef)}/forms/${encodeURIComponent(fid)}/confirmations`, { headers: feeEarner() }),
  recordConfirmation: (matterRef, fid, body) =>
    axios.post(`${BASE}/matters/${encodeURIComponent(matterRef)}/forms/${encodeURIComponent(fid)}/confirmations`, body, { headers: feeEarner() }),

  // BM-H3584: the filled official OPG LP1H / LP1F (and continuation sheets)
  // for the fee earner to print. The manifest call fills and checks the
  // forms first and says what is left to do by hand; a 422 there names what
  // could not be put on the forms.
  getLpaFormsManifest: (fid) =>
    axios.get(`${BASE}/lpa/${fid}/forms`, { headers: feeEarner() }),

  // BM-H3597: fill the OPG forms and file them to the matter's Drafts folder.
  // 200 only when every upload was confirmed; 422 / 500 / 502 name what failed.
  fileLpaFormsToDrafts: (fid) =>
    axios.post(`${BASE}/lpa/${fid}/forms/file-to-drafts`, {}, { headers: feeEarner() }),

  downloadLpaMainPdf: (fid) =>
    axios.get(`${BASE}/lpa/${fid}/forms/main.pdf`, {
      headers: feeEarner(),
      responseType: "blob",
    }),

  downloadLpaContinuationPdf: (fid) =>
    axios.get(`${BASE}/lpa/${fid}/forms/continuation.pdf`, {
      headers: feeEarner(),
      responseType: "blob",
    }),

  // BM-H3671: the notice to the nth person to be told (1 is the first) and the
  // application for help with the fee. 404 means the LPA does not need it.
  downloadLpaLp3Pdf: (fid, index) =>
    axios.get(`${BASE}/lpa/${fid}/forms/lp3/${index}`, {
      headers: feeEarner(),
      responseType: "blob",
    }),

  // BM-H3672: the one page "how to sign your LPA" sheet for the client.
  downloadLpaSigningSheetPdf: (fid) =>
    axios.get(`${BASE}/lpa/${fid}/forms/signing-sheet.pdf`, {
      headers: feeEarner(),
      responseType: "blob",
    }),

  downloadLpaLpa120Pdf: (fid) =>
    axios.get(`${BASE}/lpa/${fid}/forms/lpa120.pdf`, {
      headers: feeEarner(),
      responseType: "blob",
    }),

  // BM-H398: the fee earner's itemised review summary, derived on read from
  // the submission (not-known answers, business follow-ups, severance
  // election, first-executor divergence, mirror split). Nothing is stored.
  getDerivedSummary: (matterRef, fid) =>
    axios.get(
      `${BASE}/matters/${encodeURIComponent(matterRef)}/derived-summary/${fid}`,
      { headers: feeEarner() }
    ),

  // Client (tokenised) endpoints
  getForm: (fid, token) =>
    axios.get(`${BASE}/${fid}`, { headers: bearer(token) }),

  patchAnswers: (fid, token, answers, dryRun = true) =>
    axios.patch(
      `${BASE}/${fid}/answers`,
      { answers, dry_run_engine: dryRun },
      { headers: bearer(token) }
    ),

  askHaiku: (fid, token, { mode, question, fieldId, parkAsSuggestion }) =>
    axios.post(
      `${BASE}/${fid}/haiku`,
      {
        mode,
        question,
        field_id: fieldId || null,
        park_as_suggestion: Boolean(parkAsSuggestion),
      },
      { headers: bearer(token) }
    ),

  // BM-H3576: the "Request a callback" button. Resolves only when the
  // backend wrote a task or sent an email for the fee earner; a 503 means
  // neither landed and the page must say so rather than promise a call.
  requestCallback: (fid, token, { section, hardStopCodes, note } = {}) =>
    axios.post(
      `${BASE}/${fid}/request-callback`,
      {
        section: section || null,
        hard_stop_codes: hardStopCodes || [],
        note: note || null,
      },
      { headers: bearer(token) }
    ),

  submitForm: (fid, token) =>
    axios.post(
      `${BASE}/${fid}/submit`,
      { confirm: true },
      { headers: bearer(token) }
    ),
};

export default clientFormsApi;
